import React, { useState, useEffect, useRef } from 'react';
import CancelWarningPopup from './CancelWarningPopup';
import ScoringSlider from './ScoringSlider';
import Tooltip from './Tooltip';
import '../../../styles/phrase-training.css';
import { PROSODY_EMOTION_CONFIG } from '../../../utils/ProsodyEmotionConfig';

const EMOTIONS = ['Happy', 'Sad', 'Angry', 'Fear', 'Disgust', 'Surprise'];
const EMOTION_TOOLTIP = 'Each emotion must be rated on a 0-3 scale where: 0 = not present; 3 = strongly present';
const SENTIMENT_TOOLTIP = 'The overall sentiment must be rated on an integer scale from -3 to +3 where: -3 = strongly negative; 0 = neutral; +3 = strongly positive';

const NON_MANUAL_MARKERS = {
    Eyebrows: ['N/A', 'Raised', 'Furrowed', 'Neutral'],
    Head: ['N/A', 'Nod', 'Shake', 'Tilt'],
    Mouth: ['N/A', 'oo', 'mm', 'cha', 'Puffed Cheeks', 'Gritted Teeth'],
    Eyes: ['N/A', 'Gaze Direction', 'Straight'],
    Nose: ['N/A', 'Wrinkled'],
    Shoulders: ['N/A', 'Raised (both)', 'Raised (one to cheek)', 'Neutral'],
};

const NON_MANUAL_TOOLTIP = 'Choose All That Apply.';

const defaultScores = EMOTIONS.reduce((acc, emotion) => ({ ...acc, [emotion]: 0 }), {});
const defaultMarkers = Object.keys(NON_MANUAL_MARKERS).reduce(
    (acc, key) => ({ ...acc, [key]: 'N/A' }),
    {}
);

const EmotionScoringModal = ({
    visible,
    onSubmit,
    onCancel,
    initialScores,
    initialSentiment,
    initialSpeed,
    initialNonManualMarkers,
    initialNotes,
    isEditing = false,
    phrase = '',
    variation = '',
    videoUrl = '',
    editingVideoId,
}) => {
    const [scores, setScores] = useState(defaultScores);
    const [sentiment, setSentiment] = useState(0);
    const [speed, setSpeed] = useState('');
    const [speedError, setSpeedError] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [nonManualMarkers, setNonManualMarkers] = useState(defaultMarkers);
    const [notes, setNotes] = useState('');
    const [step, setStep] = useState(1);
    const [disabledEmotions, setDisabledEmotions] = useState([]);
    const modalRef = useRef(null);

    useEffect(() => {
        if (visible) {
            const { scorable = EMOTIONS, baseline = {} } =
                PROSODY_EMOTION_CONFIG[variation] || {};
            setScores(
                initialScores
                    ? { ...defaultScores, ...initialScores }
                    : { ...defaultScores, ...baseline }
            );
            setDisabledEmotions(EMOTIONS.filter((e) => !scorable.includes(e)));
            setSentiment(initialSentiment ?? 0);
            setSpeed(initialSpeed ?? '');
            setSpeedError(false);
            setNonManualMarkers(
                initialNonManualMarkers
                    ? { ...defaultMarkers, ...initialNonManualMarkers }
                    : defaultMarkers
            );
            setNotes(initialNotes ?? '');
            setStep(1);
        }
    }, [
        visible,
        editingVideoId,
        variation,
        initialScores,
        initialSentiment,
        initialSpeed,
        initialNonManualMarkers,
        initialNotes,
    ]);

    useEffect(() => {
        if (step === 2 && modalRef.current) {
            modalRef.current.scrollTop = 0; // optional: jump to top when entering step 2
        }
    }, [step]);

    const handleScoreChange = (emotion, value) => {
        setScores({ ...scores, [emotion]: Number(value) });
    };

    const handleSubmit = () => {
        if (!speed) {
            setSpeedError(true);
            setStep(1);
            return;
        }
        const transformedMarkers = Object.fromEntries(
            Object.entries(nonManualMarkers).map(([key, value]) => [
                key.toLowerCase(),
                value,
            ])
        );
        onSubmit({
            scores,
            sentiment,
            speed,
            non_manuals: transformedMarkers,
            notes,
        });
    };

    if (!visible) return null;

    return (
        <div className="phrase-modal-overlay">
            <div
                ref={modalRef}
                className={`phrase-modal ${step === 2 ? 'scroll-on-step2' : ''}`}
            >
                <h3 className="phrase-modal-title">Emotion Scoring</h3>
                <h3 className="sample-popup-title"><strong>PHRASE:</strong> {phrase}</h3>
                <div className="sample-popup-variation"><strong>VARIATION:</strong> {variation}</div>
                {videoUrl && (
                    <video
                        key={videoUrl}
                        src={videoUrl}
                        loop
                        autoPlay
                        muted
                        className="sample-popup-video"
                    />
                )}
                {!isEditing && (
                    <p className="emotion-warning">
                        <img
                            src="/icons/caution.svg"
                            alt="Warning"
                            className="warning-icon"
                        />
                        WARNING: If you leave the page before submitting your score, your video will be deleted.
                    </p>
                )}
                {step === 1 ? (
                    <>
                        {EMOTIONS.map((emotion) => (
                            <ScoringSlider
                                key={emotion}
                                label={emotion}
                                min={0}
                                max={3}
                                value={scores[emotion]}
                                onChange={(val) => handleScoreChange(emotion, val)}
                                tooltip={EMOTION_TOOLTIP}
                                disabled={disabledEmotions.includes(emotion)}
                            />
                        ))}
                        <ScoringSlider
                            label="Overall Sentiment"
                            min={-3}
                            max={3}
                            value={sentiment}
                            onChange={setSentiment}
                            tooltip={SENTIMENT_TOOLTIP}
                        />
                        <div className="speed-select">
                            <select
                                id="speed-select"
                                value={speed}
                                onChange={(e) => {
                                    setSpeed(e.target.value);
                                    if (speedError && e.target.value) setSpeedError(false);
                                }}
                                className={`gesture-dropdown${speedError ? ' error' : ''}`}
                            >
                                <option value="">Select speed</option>
                                <option value="normal">Normal</option>
                                <option value="quick">Quick</option>
                                <option value="slow">Slow</option>
                            </select>
                        </div>
                    </>
                ) : (
                    <div className="non-manual-section">
                        <label className="slider-label">
                            Non-manual Markers (sentence level)
                            <Tooltip content={NON_MANUAL_TOOLTIP}>
                                <span className="tooltip-icon">i</span>
                            </Tooltip>
                        </label>
                        {Object.entries(NON_MANUAL_MARKERS).map(([marker, options]) => (
                            <div key={marker} className="slider-row">
                                <label className="slider-label">{marker}</label>
                                <select
                                    className="gesture-dropdown"
                                    value={nonManualMarkers[marker]}
                                    onChange={(e) =>
                                        setNonManualMarkers({
                                            ...nonManualMarkers,
                                            [marker]: e.target.value,
                                        })
                                    }
                                >
                                    {options.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ))}
                        <div className="notes-section">
                            <label className="slider-label">
                                Notes (optional)
                                <Tooltip content="Add any other non-manual markers here that were not covered in the options above.">
                                    <span className="tooltip-icon">i</span>
                                </Tooltip>
                            </label>
                            <textarea
                                className="notes-textarea"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>
                    </div>
                )}
                <div className="phrase-modal-buttons">
                    <button
                        className="phrase-button secondary"
                        onClick={() => setShowCancelConfirm(true)}
                    >
                        {isEditing ? 'Cancel Changes' : 'Cancel/Retry'}
                    </button>
                    {step === 1 && speed && (
                        <button
                            className="phrase-button primary"
                            onClick={() => setStep(2)}
                        >
                            Next
                        </button>
                    )}
                    {step === 2 && (
                        <>
                            <button
                                className="phrase-button secondary"
                                onClick={() => setStep(1)}
                            >
                                Back
                            </button>
                            <button
                                className="phrase-button primary"
                                onClick={handleSubmit}
                            >
                                Submit
                            </button>
                        </>
                    )}
                </div>
            </div>
            {showCancelConfirm && (
                <CancelWarningPopup
                    message={
                        isEditing
                            ? 'Canceling will delete your rating changes. Are you sure?'
                            : 'Canceling will delete this recording. Are you sure?'
                    }
                    onConfirm={() => {
                        setShowCancelConfirm(false);
                        onCancel();
                    }}
                    onClose={() => setShowCancelConfirm(false)}
                />
            )}
        </div>
    );
};

export default EmotionScoringModal;
